import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Autocomplete,
    TextField,
    CircularProgress,
    MenuItem,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    IconButton,
    Tooltip,
    InputAdornment,
    Radio,
    Typography,
    LinearProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SchoolIcon from '@mui/icons-material/School';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import domtoimage from 'dom-to-image-chrome-fix-retina';

import StandardPageWrapper from '../../components/StandardPageWrapper/StandardPageWrapper';
import StandardButton from '../../components/StandardButton/StandardButton';
import CustomMuiSelect from '../../components/CustomMuiSelect/CustomMuiSelect';
import StandardPreviewCardDownload from '../../components/StandardPreviewCardDownload/StandardPreviewCardDownload';
import StandardOutlinedTextfield from '../../components/StandardOutlinedTextfield/StandardOutlinedTextfield';
import { generateIdCardsPdf } from '../../utils/idCardPdfGenerator';
import './DownloadCards.css';

function DownloadCards() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Active tab: 'single' | 'multiple' | 'class'
    const currentTab = searchParams.get('tab') || 'single';

    // User session
    const userRole = sessionStorage.getItem('ID_CardAdmin_user_role');
    const isSchoolUser = userRole === 'school';
    let schoolUser = null;
    if (isSchoolUser) {
        try {
            schoolUser = JSON.parse(sessionStorage.getItem('ID_CardAdmin_user'));
        } catch (e) {
            console.error('Failed to parse schoolUser:', e);
        }
    }

    // School selection
    const [allSchools, setAllSchools] = useState([]);
    const [allSchoolsLoading, setAllSchoolsLoading] = useState(false);
    const [selectedSchoolId, setSelectedSchoolId] = useState(schoolUser?.schoolid || '');
    const [selectedSchoolName, setSelectedSchoolName] = useState(schoolUser?.name || '');

    // School Credits
    const [schoolCredits, setSchoolCredits] = useState(null);
    const [creditsLoading, setCreditsLoading] = useState(false);

    // Classes for selected school
    const [classesList, setClassesList] = useState([]);

    // Raw mapped card data from /user/getmapped_data_byschoolid
    const [mappedData, setMappedData] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    // Search & Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [classFilter, setClassFilter] = useState('all');
    const [verificationFilter, setVerificationFilter] = useState('all'); // 'all', '1', '0'
    const [downloadFilter, setDownloadFilter] = useState('all'); // 'all', 'downloaded', 'not_downloaded'

    // Single card selection
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [singleDownloading, setSingleDownloading] = useState(false);

    // Multiple cards selection: map of mapped_id -> boolean
    const [selectedCheckboxMap, setSelectedCheckboxMap] = useState({});

    // Download modal, format & loading
    const [openCountModal, setOpenCountModal] = useState(false);
    const [bulkDownloadType, setBulkDownloadType] = useState('selected'); // 'single' | 'selected' | 'class_all'
    const [downloadFormat, setDownloadFormat] = useState('pdf'); // 'pdf' | 'zip'
    const [singleStudentToDownload, setSingleStudentToDownload] = useState(null);
    const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0, text: '' });
    const [startCount, setStartCount] = useState({ oneSideFront: 1, bothSideFront: 1, bothSideBack: 1 });
    const [bulkDownloading, setBulkDownloading] = useState(false);

    // Fetch all schools if admin
    useEffect(() => {
        if (!isSchoolUser) {
            setAllSchoolsLoading(true);
            axios.get(`${process.env.REACT_APP_BASE_URL}/user/getallschoolfordropdown`)
                .then(res => {
                    if (res.data.success) {
                        setAllSchools(res.data.data || []);
                    }
                })
                .catch(err => console.error(err))
                .finally(() => setAllSchoolsLoading(false));
        }
    }, [isSchoolUser]);

    // When school changes, fetch credits, classes, and mapped card data
    useEffect(() => {
        if (!selectedSchoolId) return;
        fetchCredits(selectedSchoolId);
        fetchClasses(selectedSchoolId);
        fetchSchoolMappedData(selectedSchoolId);
    }, [selectedSchoolId]);

    const fetchCredits = async (schoolId) => {
        setCreditsLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/user/get-school-credits/${schoolId}`);
            if (res.data.success) {
                setSchoolCredits(res.data.credits);
            }
        } catch (err) {
            console.error('Error fetching credits:', err);
        } finally {
            setCreditsLoading(false);
        }
    };

    const fetchClasses = async (schoolId) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/user/getclassesbyschoolid`, { schoolid: schoolId });
            if (res.data.success) {
                setClassesList(res.data.data || []);
            }
        } catch (err) {
            console.error('Error fetching classes:', err);
        }
    };

    const fetchSchoolMappedData = async (schoolId) => {
        setLoadingData(true);
        setSelectedStudent(null);
        setSelectedCheckboxMap({});
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/user/getmapped_data_byschoolid`, { school_id: schoolId });
            if (res.data.success) {
                setMappedData(res.data.data || []);
            } else {
                setMappedData([]);
                enqueueSnackbar(res.data.message || 'No card templates found for this school', { variant: 'info' });
            }
        } catch (err) {
            console.error('Error fetching school mapped data:', err);
            enqueueSnackbar('Error loading school cards', { variant: 'error' });
        } finally {
            setLoadingData(false);
        }
    };

    // Class lookup map: classid -> "standard - division"
    const classLookup = useMemo(() => {
        const map = {};
        classesList.forEach(c => {
            const label = c.division ? `${c.standard} - ${c.division}` : `${c.standard}`;
            map[c.classid] = label;
        });
        return map;
    }, [classesList]);

    // Flatten all students across all ID card templates for this school
    const allStudents = useMemo(() => {
        const list = [];
        (mappedData || []).forEach(templateItem => {
            const idCard = templateItem.id_card;
            (templateItem.user_data || []).forEach(u => {
                // Class name resolution
                const className = classLookup[u.class_id] || u.user_details?.class || 'Class ' + (u.class_id || '');
                
                // Searchable text string covering all student details
                const detailsValues = Object.values(u.user_details || {}).join(' ');
                const searchableText = `${u.name || ''} ${u.last_name || ''} ${u.phone || ''} ${u.mapped_id || ''} ${u.user_id || ''} ${className} ${detailsValues}`.toLowerCase();

                list.push({
                    ...u,
                    template: idCard,
                    className: className,
                    searchableText: searchableText
                });
            });
        });
        return list;
    }, [mappedData, classLookup]);

    // Filter students based on search query, class, verification, and download status
    const filteredStudents = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        return allStudents.filter(s => {
            // Text search across entire school
            if (query && !s.searchableText.includes(query)) {
                return false;
            }

            // Class filter
            if (classFilter !== 'all' && String(s.class_id) !== String(classFilter)) {
                return false;
            }

            // Verification filter
            if (verificationFilter === '1' && s.is_verified !== 1) {
                return false;
            }
            if (verificationFilter === '0' && s.is_verified === 1) {
                return false;
            }

            // Downloaded status filter
            if (downloadFilter === 'downloaded' && s.is_downloaded !== 1) {
                return false;
            }
            if (downloadFilter === 'not_downloaded' && s.is_downloaded === 1) {
                return false;
            }

            return true;
        });
    }, [allStudents, searchQuery, classFilter, verificationFilter, downloadFilter]);

    // Currently selected student count for multiple download
    const selectedStudentsList = useMemo(() => {
        return filteredStudents.filter(s => selectedCheckboxMap[s.mapped_id]);
    }, [filteredStudents, selectedCheckboxMap]);

    // Calculate required credits for selected students
    const requiredCreditsForSelection = useMemo(() => {
        return selectedStudentsList.filter(s => s.is_downloaded !== 1).length;
    }, [selectedStudentsList]);

    // Toggle single card checkbox
    const handleToggleCardCheckbox = (mappedId) => {
        setSelectedCheckboxMap(prev => ({
            ...prev,
            [mappedId]: !prev[mappedId]
        }));
    };

    // Toggle select all filtered
    const handleSelectAllFiltered = (e) => {
        const checked = e.target.checked;
        const newMap = { ...selectedCheckboxMap };
        filteredStudents.forEach(s => {
            newMap[s.mapped_id] = checked;
        });
        setSelectedCheckboxMap(newMap);
    };

    const isAllFilteredSelected = filteredStudents.length > 0 && filteredStudents.every(s => selectedCheckboxMap[s.mapped_id]);

    // ------------------------------------------------------------------------
    // TARGET STUDENTS & CREDITS MEMO FOR DOWNLOAD MODAL
    // ------------------------------------------------------------------------
    const modalTargetStudents = useMemo(() => {
        if (bulkDownloadType === 'single') {
            return singleStudentToDownload ? [singleStudentToDownload] : [];
        }
        if (bulkDownloadType === 'selected') {
            return selectedStudentsList;
        }
        if (bulkDownloadType === 'class_all') {
            return filteredStudents;
        }
        return [];
    }, [bulkDownloadType, singleStudentToDownload, selectedStudentsList, filteredStudents]);

    const modalRequiredCredits = useMemo(() => {
        return modalTargetStudents.filter(s => s.is_downloaded !== 1).length;
    }, [modalTargetStudents]);

    // Open download dialog for single, selected, or class
    const handleOpenDownloadModal = (type = 'selected', student = null) => {
        setBulkDownloadType(type);
        setSingleStudentToDownload(student);
        setDownloadProgress({ current: 0, total: 0, text: '' });
        setOpenCountModal(true);
    };

    // Quick or single card download trigger
    const handleDownloadSingleCard = (student) => {
        if (!student) return;
        handleOpenDownloadModal('single', student);
    };

    // ------------------------------------------------------------------------
    // UNIFIED DOWNLOAD HANDLER (PDF SHEET & ZIP ARCHIVE)
    // ------------------------------------------------------------------------
    const handleStartBulkDownload = async () => {
        let targetStudents = modalTargetStudents;

        if (targetStudents.length === 0) {
            enqueueSnackbar('Please select at least one card to download!', { variant: 'warning' });
            return;
        }

        // Filter verified cards for production download
        const verifiedStudents = targetStudents.filter(s => s.is_verified === 1);
        const unverifiedCount = targetStudents.length - verifiedStudents.length;

        if (verifiedStudents.length === 0) {
            enqueueSnackbar('None of the selected cards are verified! Only verified cards can be downloaded.', { variant: 'error' });
            return;
        }

        if (unverifiedCount > 0) {
            enqueueSnackbar(`${unverifiedCount} unverified card(s) will be skipped.`, { variant: 'warning' });
        }

        setBulkDownloading(true);
        setDownloadProgress({ current: 0, total: verifiedStudents.length, text: 'Verifying school credits...' });

        try {
            // 1. Credit Pre-check for any not yet downloaded cards
            const unDownloadedCards = verifiedStudents.filter(u => u.is_downloaded !== 1);
            if (unDownloadedCards.length > 0) {
                const checkRes = await axios.post(`${process.env.REACT_APP_BASE_URL}/user/check-download-credits`, {
                    school_id: selectedSchoolId,
                    ids: verifiedStudents.map(u => u.mapped_id)
                });

                if (!checkRes.data.success || !checkRes.data.can_download) {
                    enqueueSnackbar(checkRes.data.message || 'Please recharge your account. Insufficient credits.', { variant: 'error' });
                    setBulkDownloading(false);
                    return;
                }
            }

            const schoolNameClean = (selectedSchoolName || 'School').replace(/[^a-zA-Z0-9_-]/g, '_');

            // 2. Perform Download according to downloadFormat
            if (downloadFormat === 'pdf') {
                setDownloadProgress({ current: 0, total: verifiedStudents.length, text: 'Preparing PDF generator...' });

                const fileName = bulkDownloadType === 'single'
                    ? `${(verifiedStudents[0].name || 'Card').replace(/[^a-zA-Z0-9_-]/g, '_')}_${verifiedStudents[0].mapped_id}_ID_Card.pdf`
                    : `${schoolNameClean}_ID_Cards.pdf`;

                await generateIdCardsPdf(verifiedStudents, {
                    fileName,
                    onProgress: (current, total, statusText) => {
                        setDownloadProgress({ current, total, text: statusText });
                    }
                });
            } else {
                // ZIP Format (PNG files with serial counts)
                const zip = new JSZip();
                let oneSideCount = parseInt(startCount.oneSideFront) || 1;
                let bothSideFrontCount = parseInt(startCount.bothSideFront) || 1;
                let bothSideBackCount = parseInt(startCount.bothSideBack) || 1;

                const oneSideFolder = zip.folder("one-side");
                const bothSideFrontFolder = zip.folder("both-side/front");
                const bothSideBackFolder = zip.folder("both-side/back");

                for (let i = 0; i < verifiedStudents.length; i++) {
                    const s = verifiedStudents[i];
                    setDownloadProgress({
                        current: i + 1,
                        total: verifiedStudents.length,
                        text: `Packaging card ${i + 1} of ${verifiedStudents.length}...`
                    });

                    const frontNode = document.getElementById(`previewcustomCard-front-${s.mapped_id}`);
                    const isBothSide = s.template?.type == 2;
                    const backNode = isBothSide ? document.getElementById(`previewcustomCard-back-${s.mapped_id}`) : null;

                    if (!frontNode) {
                        console.warn(`Card node for ${s.mapped_id} not found in DOM`);
                        continue;
                    }

                    if (!isBothSide) {
                        const frontDataUrl = await domtoimage.toPng(frontNode, { cacheBust: true });
                        oneSideFolder.file(`${oneSideCount}.png`, frontDataUrl.split(",")[1], { base64: true });
                        oneSideCount++;
                    } else {
                        const frontDataUrl = await domtoimage.toPng(frontNode, { cacheBust: true });
                        bothSideFrontFolder.file(`${bothSideFrontCount}.png`, frontDataUrl.split(",")[1], { base64: true });
                        bothSideFrontCount++;

                        if (backNode) {
                            const backDataUrl = await domtoimage.toPng(backNode, { cacheBust: true });
                            bothSideBackFolder.file(`${bothSideBackCount}.png`, backDataUrl.split(",")[1], { base64: true });
                            bothSideBackCount++;
                        }
                    }
                }

                setDownloadProgress(prev => ({ ...prev, text: 'Compressing ZIP archive...' }));
                const zipContent = await zip.generateAsync({ type: 'blob' });
                const zipFileName = bulkDownloadType === 'single'
                    ? `${(verifiedStudents[0].name || 'Card').replace(/[^a-zA-Z0-9_-]/g, '_')}_${verifiedStudents[0].mapped_id}_IDCard.zip`
                    : `${schoolNameClean}_ID_Cards.zip`;

                saveAs(zipContent, zipFileName);
            }

            // 3. Update server download status and debit credits
            setDownloadProgress(prev => ({ ...prev, text: 'Updating download status and credits...' }));
            const idsToUpdate = verifiedStudents.map(s => s.mapped_id);
            const statusRes = await axios.post(`${process.env.REACT_APP_BASE_URL}/user/updatedownloadstatus`, {
                ids: idsToUpdate
            });

            if (statusRes.data.success) {
                if (statusRes.data.debited_credits > 0) {
                    enqueueSnackbar(`Downloaded successfully! ${statusRes.data.debited_credits} credit(s) debited.`, { variant: 'success' });
                } else {
                    enqueueSnackbar(`Downloaded successfully! (0 credits charged - previously downloaded)`, { variant: 'success' });
                }

                // Update local flags
                verifiedStudents.forEach(s => {
                    s.is_downloaded = 1;
                });
                if (selectedStudent) {
                    setSelectedStudent(prev => prev ? { ...prev, is_downloaded: 1 } : null);
                }
                fetchCredits(selectedSchoolId);
            }

            setOpenCountModal(false);
        } catch (err) {
            console.error('Error during download:', err);
            enqueueSnackbar(err.response?.data?.message || 'Error occurred during download. Please try again.', { variant: 'error' });
        } finally {
            setBulkDownloading(false);
            setDownloadProgress({ current: 0, total: 0, text: '' });
        }
    };

    return (
        <StandardPageWrapper header="Download Cards">
            <div className="downloadCards_container">
                {/* TOP HEADER: School Selector / Info & Live Credits & Tab Switcher */}
                <div className="downloadCards_topHeader">
                    <div className="downloadCards_schoolInfo">
                        {isSchoolUser ? (
                            <Chip
                                icon={<SchoolIcon />}
                                label={schoolUser?.name || 'School Portal'}
                                sx={{ fontWeight: 700, fontSize: '15px', padding: '6px 4px', backgroundColor: '#e0e7ff', color: '#3730a3' }}
                            />
                        ) : (
                            <Autocomplete
                                options={allSchools}
                                getOptionLabel={(option) => option.name || ''}
                                value={allSchools.find(s => s.schoolid === selectedSchoolId) || null}
                                onChange={(e, newValue) => {
                                    if (newValue) {
                                        setSelectedSchoolId(newValue.schoolid);
                                        setSelectedSchoolName(newValue.name);
                                    } else {
                                        setSelectedSchoolId('');
                                        setSelectedSchoolName('');
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select School"
                                        size="small"
                                        sx={{ width: 260, backgroundColor: '#ffffff' }}
                                    />
                                )}
                                loading={allSchoolsLoading}
                            />
                        )}

                        {/* Credits Badge */}
                        {selectedSchoolId && (
                            <div className={`downloadCards_creditBadge ${schoolCredits === 0 ? 'downloadCards_creditBadge_low' : ''}`}>
                                <AccountBalanceWalletIcon fontSize="small" />
                                <span>Credits: {creditsLoading ? '...' : (schoolCredits !== null ? schoolCredits : '0')}</span>
                                <Tooltip title="Refresh credit balance">
                                    <IconButton size="small" onClick={() => fetchCredits(selectedSchoolId)} sx={{ padding: '2px' }}>
                                        <RefreshIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        )}
                    </div>
                </div>

                {!selectedSchoolId ? (
                    <div className="downloadCards_emptyState">
                        <SchoolIcon sx={{ fontSize: 60, color: '#9ca3af', marginBottom: 2 }} />
                        <h3>Please select a school to view and download ID cards.</h3>
                    </div>
                ) : loadingData ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <CircularProgress size={40} sx={{ color: '#7978e9' }} />
                        <p style={{ marginTop: 12, color: '#6b7280' }}>Loading school cards and students...</p>
                    </div>
                ) : (
                    <>
                        {/* ============================================================== */}
                        {/* TAB 1: SINGLE CARD DOWNLOAD                                    */}
                        {/* ============================================================== */}
                        {currentTab === 'single' && (
                            <div className="downloadCards_singleCont">
                                {/* Search Bar & Quick Filters */}
                                <div className="downloadCards_filterBar">
                                    <TextField
                                        label="Search student across entire school (Name, Roll No, Adm No, Phone, ID...)"
                                        variant="outlined"
                                        size="small"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        sx={{ flex: 1, minWidth: 280 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="action" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchQuery && (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />

                                    <CustomMuiSelect
                                        label="Class"
                                        val={classFilter}
                                        onChange={(e) => setClassFilter(e.target.value)}
                                        width={160}
                                        size="small"
                                    >
                                        <MenuItem value="all">All Classes</MenuItem>
                                        {classesList.map(c => (
                                            <MenuItem key={c.classid} value={c.classid}>
                                                {c.division ? `${c.standard} - ${c.division}` : c.standard}
                                            </MenuItem>
                                        ))}
                                    </CustomMuiSelect>

                                    <CustomMuiSelect
                                        label="Verification"
                                        val={verificationFilter}
                                        onChange={(e) => setVerificationFilter(e.target.value)}
                                        width={140}
                                        size="small"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="1">Verified</MenuItem>
                                        <MenuItem value="0">Not Verified</MenuItem>
                                    </CustomMuiSelect>

                                    <CustomMuiSelect
                                        label="Download Status"
                                        val={downloadFilter}
                                        onChange={(e) => setDownloadFilter(e.target.value)}
                                        width={160}
                                        size="small"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="downloaded">Downloaded</MenuItem>
                                        <MenuItem value="not_downloaded">Not Downloaded</MenuItem>
                                    </CustomMuiSelect>
                                </div>

                                {/* Single Card Preview or Selection List */}
                                {selectedStudent ? (
                                    <div className="downloadCards_singlePreviewBox">
                                        <div className="downloadCards_singleStudentHeader">
                                            <div>
                                                <h2 style={{ margin: '0 0 6px 0', color: '#1f2937' }}>
                                                    {selectedStudent.name} {selectedStudent.last_name || ''}
                                                </h2>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <Chip label={selectedStudent.className} size="small" variant="outlined" />
                                                    {selectedStudent.user_details?.roll_no && (
                                                        <Chip label={`Roll: ${selectedStudent.user_details.roll_no}`} size="small" variant="outlined" />
                                                    )}
                                                    {selectedStudent.is_verified === 1 ? (
                                                        <Chip label="Verified" size="small" color="success" />
                                                    ) : (
                                                        <Chip label="Not Verified" size="small" color="warning" />
                                                    )}
                                                    {selectedStudent.is_downloaded === 1 ? (
                                                        <Chip label="Downloaded" size="small" color="info" />
                                                    ) : (
                                                        <Chip label="Not Downloaded" size="small" color="default" />
                                                    )}
                                                    {selectedStudent.is_downloaded === 1 ? (
                                                        <Chip label="Cost: 0 Credits (Re-download)" size="small" sx={{ backgroundColor: '#f0fdf4', color: '#166534', fontWeight: 600 }} />
                                                    ) : (
                                                        <Chip label="Cost: 1 Credit" size="small" sx={{ backgroundColor: '#eff6ff', color: '#1e40af', fontWeight: 600 }} />
                                                    )}
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setSelectedStudent(null)}
                                                >
                                                    Choose Another Student
                                                </Button>
                                                <StandardButton
                                                    variant="contained"
                                                    label="Download Card"
                                                    startIcon={<DownloadIcon />}
                                                    onClick={() => handleDownloadSingleCard(selectedStudent)}
                                                    loading={singleDownloading}
                                                    style={{ background: '#7978e9 !important', color: '#ffffff' }}
                                                />
                                            </div>
                                        </div>

                                        {/* Card Render */}
                                        <div style={{ margin: '20px auto' }}>
                                            <StandardPreviewCardDownload
                                                custom_uid={selectedStudent.mapped_id}
                                                cardData={selectedStudent.template}
                                                userData={selectedStudent}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ marginBottom: 12, color: '#4b5563', fontWeight: 600 }}>
                                            Found {filteredStudents.length} student(s) across entire school. Click "Preview & Download" to select a card.
                                        </div>

                                        {filteredStudents.length === 0 ? (
                                            <div className="downloadCards_emptyState">
                                                <p>No students match the current search or filters.</p>
                                            </div>
                                        ) : (
                                            <div className="downloadCards_searchResultsTable">
                                                {filteredStudents.slice(0, 50).map(s => (
                                                    <div key={s.mapped_id} className="downloadCards_searchRow">
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}>
                                                                {s.name} {s.last_name || ''}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <span style={{ color: '#4b5563', fontSize: '13px' }}>{s.className}</span>
                                                                {s.user_details?.roll_no && (
                                                                    <span style={{ color: '#6b7280', fontSize: '13px' }}>• Roll: {s.user_details.roll_no}</span>
                                                                )}
                                                                {s.phone && (
                                                                    <span style={{ color: '#6b7280', fontSize: '13px' }}>• Ph: {s.phone}</span>
                                                                )}
                                                                {s.is_verified === 1 ? (
                                                                    <Chip label="Verified" size="small" color="success" sx={{ height: 20, fontSize: '11px' }} />
                                                                ) : (
                                                                    <Chip label="Unverified" size="small" color="warning" sx={{ height: 20, fontSize: '11px' }} />
                                                                )}
                                                                {s.is_downloaded === 1 ? (
                                                                    <Chip label="Downloaded" size="small" color="info" sx={{ height: 20, fontSize: '11px' }} />
                                                                ) : (
                                                                    <Chip label="Not Downloaded" size="small" color="default" sx={{ height: 20, fontSize: '11px' }} />
                                                                )}
                                                            </div>
                                                        </div>

                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={() => setSelectedStudent(s)}
                                                            sx={{ backgroundColor: '#7978e9 !important', textTransform: 'none' }}
                                                        >
                                                            Preview & Download
                                                        </Button>
                                                    </div>
                                                ))}

                                                {filteredStudents.length > 50 && (
                                                    <div style={{ padding: '12px 16px', textAlign: 'center', color: '#6b7280', background: '#f9fafb' }}>
                                                        Showing top 50 results. Please narrow down your search query for more specific matches.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* ============================================================== */}
                        {/* TAB 2: MULTIPLE CARDS DOWNLOAD                                 */}
                        {/* ============================================================== */}
                        {currentTab === 'multiple' && (
                            <div>
                                {/* Filters */}
                                <div className="downloadCards_filterBar">
                                    <TextField
                                        label="Search across entire school..."
                                        variant="outlined"
                                        size="small"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        sx={{ flex: 1, minWidth: 260 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon color="action" />
                                                </InputAdornment>
                                            )
                                        }}
                                    />

                                    <CustomMuiSelect
                                        label="Class"
                                        val={classFilter}
                                        onChange={(e) => setClassFilter(e.target.value)}
                                        width={160}
                                        size="small"
                                    >
                                        <MenuItem value="all">All Classes</MenuItem>
                                        {classesList.map(c => (
                                            <MenuItem key={c.classid} value={c.classid}>
                                                {c.division ? `${c.standard} - ${c.division}` : c.standard}
                                            </MenuItem>
                                        ))}
                                    </CustomMuiSelect>

                                    <CustomMuiSelect
                                        label="Verification"
                                        val={verificationFilter}
                                        onChange={(e) => setVerificationFilter(e.target.value)}
                                        width={140}
                                        size="small"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="1">Verified</MenuItem>
                                        <MenuItem value="0">Not Verified</MenuItem>
                                    </CustomMuiSelect>

                                    <CustomMuiSelect
                                        label="Download Status"
                                        val={downloadFilter}
                                        onChange={(e) => setDownloadFilter(e.target.value)}
                                        width={160}
                                        size="small"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="downloaded">Downloaded</MenuItem>
                                        <MenuItem value="not_downloaded">Not Downloaded</MenuItem>
                                    </CustomMuiSelect>
                                </div>

                                {/* Selection Bar */}
                                <div className="downloadCards_selectionBar">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Checkbox
                                            checked={isAllFilteredSelected}
                                            indeterminate={selectedStudentsList.length > 0 && !isAllFilteredSelected}
                                            onChange={handleSelectAllFiltered}
                                        />
                                        <span style={{ fontWeight: 600, color: '#374151' }}>
                                            Select All Filtered ({filteredStudents.length})
                                        </span>
                                        <Chip
                                            label={`${selectedStudentsList.length} Selected`}
                                            color="primary"
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                        <Chip
                                            label={`Required Credits: ${requiredCreditsForSelection}`}
                                            color={requiredCreditsForSelection > (schoolCredits || 0) ? 'error' : 'success'}
                                            size="small"
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </div>

                                    <StandardButton
                                        variant="contained"
                                        label={`Download Selected (${selectedStudentsList.length})`}
                                        startIcon={<DownloadIcon />}
                                        onClick={() => {
                                            if (selectedStudentsList.length === 0) {
                                                return enqueueSnackbar('Please select at least one card!', { variant: 'warning' });
                                            }
                                            handleOpenDownloadModal('selected');
                                        }}
                                        loading={bulkDownloading}
                                        style={{ background: '#7978e9 !important', color: '#ffffff' }}
                                    />
                                </div>

                                {/* Grid of Cards */}
                                {filteredStudents.length === 0 ? (
                                    <div className="downloadCards_emptyState">
                                        <p>No cards found matching the criteria.</p>
                                    </div>
                                ) : (
                                    <div className="downloadCards_grid">
                                        {filteredStudents.map(u => {
                                            const isSelected = !!selectedCheckboxMap[u.mapped_id];
                                            return (
                                                <div
                                                    key={u.mapped_id}
                                                    className={`downloadCards_cardWrapper ${isSelected ? 'downloadCards_cardWrapper_selected' : ''}`}
                                                >
                                                    <div className="downloadCards_cardHeader">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() => handleToggleCardCheckbox(u.mapped_id)}
                                                            />
                                                            <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                                                {u.name} {u.last_name || ''}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 4 }}>
                                                            {u.is_verified === 1 ? (
                                                                <Chip label="Verified" size="small" color="success" sx={{ height: 20, fontSize: '10px' }} />
                                                            ) : (
                                                                <Chip label="Unverified" size="small" color="warning" sx={{ height: 20, fontSize: '10px' }} />
                                                            )}
                                                            {u.is_downloaded === 1 ? (
                                                                <Chip label="DL" size="small" color="info" sx={{ height: 20, fontSize: '10px' }} />
                                                            ) : (
                                                                <Chip label="New" size="small" color="default" sx={{ height: 20, fontSize: '10px' }} />
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="downloadCards_cardPreview">
                                                        <StandardPreviewCardDownload
                                                            custom_uid={u.mapped_id}
                                                            cardData={u.template}
                                                            userData={u}
                                                        />
                                                    </div>

                                                    <div className="downloadCards_cardFooter">
                                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                            {u.className} {u.user_details?.roll_no ? `• Roll: ${u.user_details.roll_no}` : ''}
                                                        </span>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() => handleDownloadSingleCard(u)}
                                                            sx={{ textTransform: 'none', fontSize: '12px', color: '#7978e9' }}
                                                        >
                                                            Quick DL
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ============================================================== */}
                        {/* TAB 3: DOWNLOAD BY CLASS                                       */}
                        {/* ============================================================== */}
                        {currentTab === 'class' && (
                            <div>
                                <div className="downloadCards_filterBar">
                                    <CustomMuiSelect
                                        label="Select Class"
                                        val={classFilter}
                                        onChange={(e) => setClassFilter(e.target.value)}
                                        width={200}
                                        size="small"
                                    >
                                        <MenuItem value="all">All Classes</MenuItem>
                                        {classesList.map(c => (
                                            <MenuItem key={c.classid} value={c.classid}>
                                                {c.division ? `${c.standard} - ${c.division}` : c.standard}
                                            </MenuItem>
                                        ))}
                                    </CustomMuiSelect>

                                    <CustomMuiSelect
                                        label="Verification"
                                        val={verificationFilter}
                                        onChange={(e) => setVerificationFilter(e.target.value)}
                                        width={140}
                                        size="small"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="1">Verified</MenuItem>
                                        <MenuItem value="0">Not Verified</MenuItem>
                                    </CustomMuiSelect>

                                    <CustomMuiSelect
                                        label="Download Status"
                                        val={downloadFilter}
                                        onChange={(e) => setDownloadFilter(e.target.value)}
                                        width={160}
                                        size="small"
                                    >
                                        <MenuItem value="all">All</MenuItem>
                                        <MenuItem value="downloaded">Downloaded</MenuItem>
                                        <MenuItem value="not_downloaded">Not Downloaded</MenuItem>
                                    </CustomMuiSelect>

                                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                                        <StandardButton
                                            variant="contained"
                                            label={`Download Selected (${selectedStudentsList.length})`}
                                            onClick={() => {
                                                if (selectedStudentsList.length === 0) {
                                                    return enqueueSnackbar('Please select at least one card!', { variant: 'warning' });
                                                }
                                                handleOpenDownloadModal('selected');
                                            }}
                                            loading={bulkDownloading}
                                            style={{ background: '#0d7a8e !important', color: '#ffffff' }}
                                        />

                                        <StandardButton
                                            variant="contained"
                                            label={`Download All (${filteredStudents.length})`}
                                            startIcon={<DownloadIcon />}
                                            onClick={() => {
                                                if (filteredStudents.length === 0) {
                                                    return enqueueSnackbar('No cards available to download!', { variant: 'warning' });
                                                }
                                                handleOpenDownloadModal('class_all');
                                            }}
                                            loading={bulkDownloading}
                                            style={{ background: 'darkgreen !important', color: '#ffffff' }}
                                        />
                                    </div>
                                </div>

                                {/* Selection Checkbox */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                                    <Checkbox
                                        checked={isAllFilteredSelected}
                                        indeterminate={selectedStudentsList.length > 0 && !isAllFilteredSelected}
                                        onChange={handleSelectAllFiltered}
                                    />
                                    <span style={{ fontSize: '14px', color: '#4b5563', fontWeight: 600 }}>
                                        Select All in this Class ({filteredStudents.length} cards)
                                    </span>
                                </div>

                                {/* Cards Grid */}
                                {filteredStudents.length === 0 ? (
                                    <div className="downloadCards_emptyState">
                                        <p>No cards found for this class filter.</p>
                                    </div>
                                ) : (
                                    <div className="downloadCards_grid">
                                        {filteredStudents.map(u => {
                                            const isSelected = !!selectedCheckboxMap[u.mapped_id];
                                            return (
                                                <div
                                                    key={u.mapped_id}
                                                    className={`downloadCards_cardWrapper ${isSelected ? 'downloadCards_cardWrapper_selected' : ''}`}
                                                >
                                                    <div className="downloadCards_cardHeader">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() => handleToggleCardCheckbox(u.mapped_id)}
                                                            />
                                                            <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                                                                {u.name} {u.last_name || ''}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 4 }}>
                                                            {u.is_verified === 1 ? (
                                                                <Chip label="Verified" size="small" color="success" sx={{ height: 20, fontSize: '10px' }} />
                                                            ) : (
                                                                <Chip label="Unverified" size="small" color="warning" sx={{ height: 20, fontSize: '10px' }} />
                                                            )}
                                                            {u.is_downloaded === 1 ? (
                                                                <Chip label="DL" size="small" color="info" sx={{ height: 20, fontSize: '10px' }} />
                                                            ) : (
                                                                <Chip label="New" size="small" color="default" sx={{ height: 20, fontSize: '10px' }} />
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="downloadCards_cardPreview">
                                                        <StandardPreviewCardDownload
                                                            custom_uid={u.mapped_id}
                                                            cardData={u.template}
                                                            userData={u}
                                                        />
                                                    </div>

                                                    <div className="downloadCards_cardFooter">
                                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                                            {u.className} {u.user_details?.roll_no ? `• Roll: ${u.user_details.roll_no}` : ''}
                                                        </span>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() => handleDownloadSingleCard(u)}
                                                            sx={{ textTransform: 'none', fontSize: '12px', color: '#7978e9' }}
                                                        >
                                                            Quick DL
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* DOWNLOAD MODAL: FORMAT (PDF / ZIP) SELECTION */}
                <Dialog
                    open={openCountModal}
                    onClose={() => { if (!bulkDownloading) setOpenCountModal(false); }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ padding: '20px 24px 12px 24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                            <span style={{ fontWeight: 700, fontSize: '18px', color: '#111827' }}>
                                {bulkDownloadType === 'single'
                                    ? `Download ID Card`
                                    : `Download ID Cards (${modalTargetStudents.length})`
                                }
                            </span>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Chip
                                    size="small"
                                    label={`${modalTargetStudents.length} Card${modalTargetStudents.length === 1 ? '' : 's'}`}
                                    sx={{ fontWeight: 600, backgroundColor: '#e0e7ff', color: '#3730a3' }}
                                />
                                {modalRequiredCredits > 0 ? (
                                    <Chip
                                        size="small"
                                        label={`Credits: ${modalRequiredCredits}`}
                                        sx={{ fontWeight: 600, backgroundColor: '#eff6ff', color: '#1d4ed8' }}
                                    />
                                ) : (
                                    <Chip
                                        size="small"
                                        label="0 Credits (Free Re-download)"
                                        sx={{ fontWeight: 600, backgroundColor: '#f0fdf4', color: '#15803d' }}
                                    />
                                )}
                            </div>
                        </div>
                        {bulkDownloadType === 'single' && singleStudentToDownload && (
                            <Typography variant="body2" sx={{ color: '#6b7280', marginTop: '4px' }}>
                                {singleStudentToDownload.name} {singleStudentToDownload.last_name || ''} • {singleStudentToDownload.className}
                            </Typography>
                        )}
                    </DialogTitle>

                    <DialogContent sx={{ padding: '12px 24px 20px 24px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', marginBottom: '8px' }}>
                            Select Download Format:
                        </Typography>

                        {/* Format Grid: PDF vs ZIP */}
                        <div className="downloadCards_formatGrid">
                            {/* Option 1: PDF Sheet */}
                            <div
                                className={`downloadCards_formatCard ${downloadFormat === 'pdf' ? 'downloadCards_formatCard_active' : ''}`}
                                onClick={() => !bulkDownloading && setDownloadFormat('pdf')}
                            >
                                <div className="downloadCards_formatHeader">
                                    <div className="downloadCards_formatTitle">
                                        <PictureAsPdfIcon sx={{ color: '#ef4444' }} />
                                        <span>PDF Sheet (Print-Ready)</span>
                                    </div>
                                    <Radio
                                        checked={downloadFormat === 'pdf'}
                                        onChange={() => setDownloadFormat('pdf')}
                                        disabled={bulkDownloading}
                                        size="small"
                                        sx={{ padding: 0 }}
                                    />
                                </div>
                                <div className="downloadCards_formatDesc">
                                    Standard <strong>21 CM × 14.85 CM</strong> landscape sheet with side-by-side cards.
                                </div>
                                <span className="downloadCards_formatTag">Recommended</span>
                            </div>

                            {/* Option 2: ZIP Archive */}
                            <div
                                className={`downloadCards_formatCard ${downloadFormat === 'zip' ? 'downloadCards_formatCard_active' : ''}`}
                                onClick={() => !bulkDownloading && setDownloadFormat('zip')}
                            >
                                <div className="downloadCards_formatHeader">
                                    <div className="downloadCards_formatTitle">
                                        <FolderZipIcon sx={{ color: '#f59e0b' }} />
                                        <span>ZIP Archive (Images)</span>
                                    </div>
                                    <Radio
                                        checked={downloadFormat === 'zip'}
                                        onChange={() => setDownloadFormat('zip')}
                                        disabled={bulkDownloading}
                                        size="small"
                                        sx={{ padding: 0 }}
                                    />
                                </div>
                                <div className="downloadCards_formatDesc">
                                    Individual PNG card images organized into front & back folders with serial numbering.
                                </div>
                                <span className="downloadCards_formatTag" style={{ background: '#fef3c7', color: '#92400e' }}>
                                    ZIP / PNG
                                </span>
                            </div>
                        </div>

                        {/* Detailed Format Configuration */}
                        {downloadFormat === 'pdf' ? (
                            <div className="downloadCards_pdfInfoBox">
                                <div className="downloadCards_pdfInfoTitle">
                                    <CheckCircleOutlineIcon fontSize="small" />
                                    <span>PDF Layout Specification:</span>
                                </div>
                                <ul className="downloadCards_pdfInfoList">
                                    <li><strong>Sheet Size:</strong> 21.0 CM (Width) × 14.85 CM (Height) - Landscape</li>
                                    <li><strong>Card Size:</strong> 5.5 CM (Width) × 8.7 CM (Height)</li>
                                    <li><strong>Arrangement:</strong> Side-by-side with <strong>Back side on the Left</strong> and <strong>Front side on the Right</strong></li>
                                    <li>
                                        <strong>Multi-Card Document:</strong> {modalTargetStudents.length > 1
                                            ? `All ${modalTargetStudents.length} cards will be compiled page-by-page into a single multi-page PDF.`
                                            : `1 page PDF ready for direct print output.`
                                        }
                                    </li>
                                </ul>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 12 }}>
                                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                    Customize starting serial numbers for image filenames inside the ZIP archive:
                                </Typography>
                                <StandardOutlinedTextfield
                                    label="One Side Start Count"
                                    name="oneSideFront"
                                    val={startCount?.oneSideFront}
                                    setVal={setStartCount}
                                    spreadData={startCount}
                                    default={1}
                                />
                                <StandardOutlinedTextfield
                                    label="Both Side Start Front Count"
                                    name="bothSideFront"
                                    val={startCount?.bothSideFront}
                                    setVal={setStartCount}
                                    spreadData={startCount}
                                />
                                <StandardOutlinedTextfield
                                    label="Both Side Back Front Count"
                                    name="bothSideBack"
                                    val={startCount?.bothSideBack}
                                    setVal={setStartCount}
                                    spreadData={startCount}
                                />
                            </div>
                        )}

                        {/* Progress Bar */}
                        {bulkDownloading && (
                            <div className="downloadCards_progressCont">
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', marginBottom: '8px' }}>
                                    {downloadProgress.text || 'Processing...'}
                                </Typography>
                                <LinearProgress
                                    variant={downloadProgress.total > 0 ? "determinate" : "indeterminate"}
                                    value={downloadProgress.total > 0 ? (downloadProgress.current / downloadProgress.total) * 100 : 0}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                                {downloadProgress.total > 0 && (
                                    <Typography variant="caption" sx={{ color: '#6b7280', marginTop: '6px', display: 'block' }}>
                                        {downloadProgress.current} / {downloadProgress.total} completed ({Math.round((downloadProgress.current / downloadProgress.total) * 100)}%)
                                    </Typography>
                                )}
                            </div>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ padding: '12px 24px 20px 24px' }}>
                        <Button
                            onClick={() => setOpenCountModal(false)}
                            disabled={bulkDownloading}
                            sx={{ textTransform: 'none', color: '#6b7280' }}
                        >
                            Cancel
                        </Button>
                        <StandardButton
                            variant="contained"
                            label={bulkDownloading ? 'Generating...' : (downloadFormat === 'pdf' ? 'Download PDF' : 'Download ZIP')}
                            startIcon={downloadFormat === 'pdf' ? <PictureAsPdfIcon /> : <FolderZipIcon />}
                            onClick={handleStartBulkDownload}
                            loading={bulkDownloading}
                            style={{
                                background: downloadFormat === 'pdf' ? '#dc2626 !important' : '#2563eb !important',
                                color: '#ffffff'
                            }}
                        />
                    </DialogActions>
                </Dialog>
            </div>
        </StandardPageWrapper>
    );
}

export default DownloadCards;
